import { useRef, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createUseStyles } from "react-jss";
import AlertTypesService from "services/AdminConsole/Monitoring/AlertTypes/AlertTypesService";
import LookupService from "services/LookupService";
import AlertRulesService from "services/AdminConsole/Monitoring/AlertRules/AlertRulesService";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiBadge,
  EuiText,
  EuiSpacer,
  EuiTextArea,
  EuiFieldText,
  EuiFieldNumber,
  EuiInMemoryTable,
  EuiFormRow,
} from "@elastic/eui";
import { Roles, Subjects } from "settings/permissions";
import ErrorsList from "components/Forms/ErrorsList";
import { Flyout } from "components/Flyout/Flyout";
import PanelHeader from "components/Panels/PanelHeader";
import { FDPanelTitle } from "components/Title/FDPanelTitle";
import Button from "components/Buttons/Button";
import ToggleButtons from "components/Buttons/ToggleButtons";
import SkeletonPlaceholder from "components/SkeletonPlaceholder";
import { isNullOrUndefined, getTableStyles, isSubjectAuthorizedForUser } from "utils";
import { DefaultPageSizeOptions, DEFAULT_NUMBER_OF_ROWS, defaultOperatorList } from "Consts";
import { toleranceToFormula } from "./AlertTypesUtils";
import {
  TOLERANCE_STATUS_COLOR,
  TABS,
  NOT_SELECTED,
  NO_DESCRIPTION,
  NO_RECOMMENDATIONS,
  rulesColumns,
} from "./AlertTypesConsts";
import IconButton from "components/Buttons/IconButton";
import AddButton from "components/Buttons/AddButton";
import ActionButtons from "components/Buttons/ActionButtons";
import FdDataTableRowActions from "components/DataTable/FdDataTableRowActions";
import { UndoIcon } from "components/Icons";
import { Card } from "components/Cards/Card";
import { SelectDropdown } from "./SelectDropdown";
import { AlertRulesDetailsFlyout } from "../AlertRules/AlertRulesDetailsFlyout";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import CustomAlerts from "./CustomAlerts";

const useStyles = createUseStyles({
  flyoutDetailsPanel: {
    marginBottom: 0,
  },
  chip: {
    border: "1px solid var(--border-color)",
    padding: "2px 6px",
    borderRadius: "25px",
    color: "var(--titile)",
    backgroundColor: "var(--card-background)",
  },
});

export const AlertTypeDetailsFlyout = ({
  selectedItem,
  profileOptions = [],
  onClose,
  canEdit = true,
}: any) => {
  const classes = useStyles();
  const createRef: any = useRef(null);
  const textAreaRef = useRef(null);
  const [editObject, setEditObject] = useState<any>(null);
  const [selectedRule, setSelectedRule] = useState<any>(null);
  const [isEditRuleMode, setEditRuleMode] = useState<boolean>(false);
  const [isEditMode, setEditMode] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const _TABS =
    selectedItem?.editMode === "CustomAlert"
      ? [
          ...TABS,
          {
            disabled: false,
            id: "Custom",
            name: "Custom",
          },
        ]
      : TABS;
  const [selectedTabId, setSelectedTabId] = useState(_TABS[0].id);

  const [selectedSeverity, setSelectedSeverity] = useState<string>(NOT_SELECTED);
  const [isSeverityChanged, setSeverityChanged] = useState<boolean>(false);
  const [severityList, setSeverityList] = useState<any>([]);

  const [categoryList, setCategoryList] = useState<any>([]);
  const [badgeCategoryList, setBadgeCategoryList] = useState<any>([]);
  const [defaultCategories, setDefaultCategories] = useState<any>([]);
  const [isCategoryChanged, setCategoryChanged] = useState<boolean>(false);

  const [description, setDescription] = useState<string>(NO_DESCRIPTION);
  const [isDescriptionChanged, setDescriptionChanged] = useState<boolean>(false);
  const [recommendations, setRecommendations] = useState<string>(NO_RECOMMENDATIONS);
  const [isRecommendationsChanged, setRecommendationsChanged] = useState<boolean>(false);
  const [thresholdsTemplate, setThresholdsTemplate] = useState<any>([]);
  const [thresholds, setThresholds] = useState<any>([]);
  const [originalThresholds, setOriginalThresholds] = useState<any>([]);
  const [editThreshold, setEditThreshold] = useState<any>(null);
  const [isThresholdChanged, setThresholdChanged] = useState<boolean>(false);
  const [operatorList, setOperatorList] = useState<any[]>(defaultOperatorList);
  const [exceptionRules, setExceptionRules] = useState<any>([]);
  const [showError, setShowError] = useState<boolean>(false);
  const [toDeleteSelected, setToDeleteSelected] = useState<any>(null);
  const [saveErrors, setSaveErrors] = useState<{ [key: string]: string }>({});

  const { clientId } = useSelector(globalFilterSelector);

  const thresholdsToUpdate = thresholds?.filter(
    (i: any) =>
      !originalThresholds.map((item: any) => JSON.stringify(item))?.includes(JSON.stringify(i))
  );

  const isNewThreshold = (id: any) => typeof id === "string" && id?.includes("new");

  const isCustomAlert = selectedItem.isCustomAlert;
  const initCustomQuery = {
    id: "",
    valueColumnName: "",
    valueName: "",
    objectColumnName: "",
    objectName: "",
    operator: "",
    messageInfoColumnName: "",
    messageFormat: "",
    evaluationFrequency: "Minute",
  };
  const [customSource, setCustomSource] = useState<any>(initCustomQuery);
  const [originalCustomSource, setOriginalCustomSource] = useState<any>(initCustomQuery);

  const isCustomSaveEnabled = [
    customSource.id,
    customSource.valueColumnName,
    customSource.valueName,
    customSource.operator,
  ]?.every((i) => i?.length > 0);

  const isCustomSourceChanged =
    Object.values(originalCustomSource)?.join("") !== Object.values(customSource)?.join("") &&
    isCustomSaveEnabled;

  const isEditModeEnabled = isSubjectAuthorizedForUser({
    subject: Subjects.Monitoring_Management,
    role: Roles.Admin,
  });

  const isSaveEnabled = [
    isDescriptionChanged,
    isRecommendationsChanged,
    isSeverityChanged,
    isCategoryChanged,
    isThresholdChanged,
    isCustomSourceChanged,
  ].some((i) => i === true);

  const getDefinition = useCallback(() => {
    setLoading(true);
    AlertTypesService.getAlertDefinition({
      clientId,
      alertTypeId: selectedItem.id,
      alertSubtypeId: selectedItem.subtypeId,
    }).then((result) => {
      setEditObject(result);
      const customQuerySource = {
        id: result?.customAlertProperties?.query?.id || "",
        valueColumnName: result?.customAlertProperties?.query?.valueColumnName || "",
        valueName: result?.customAlertProperties?.query?.valueName || "",
        objectColumnName: result?.customAlertProperties?.query?.objectColumnName || "",
        objectName: result?.customAlertProperties?.query?.objectName || "",
        operator: result?.customAlertProperties?.operator || "",
        messageInfoColumnName: result?.customAlertProperties?.query?.messageInfoColumnName || "",
        messageFormat: result?.customAlertProperties?.messageFormat || "",
        evaluationFrequency: result?.customAlertProperties?.evaluationFrequency || "Minute",
      };
      setOriginalCustomSource(customQuerySource);
      setCustomSource(customQuerySource);
      setDefaultCategories(result?.properties?.categoryAssignments || []);
      setDescription(result?.properties?.description || NO_DESCRIPTION);
      setSelectedSeverity(result?.properties?.severity?.name || NOT_SELECTED);
      setRecommendations(result?.properties?.recommendations || NO_RECOMMENDATIONS);

      const initThresholds = result?.alertThresholds?.length > 0 ? result?.alertThresholds : [];

      setOriginalThresholds(initThresholds);
      setThresholds(initThresholds);
      setThresholdsTemplate(
        result?.alertThresholds?.length > 0
          ? result?.alertThresholds
          : [result?.alertThresholdTemplate]
      );
      const alertRulesData =
        result?.exceptionRules?.map((i: any) => ({
          id: i.id,
          name: i.name,
          alertType: i?.scope?.alertAssignment?.name,
          objects: i?.scope?.objectAssignments || [],
          resources: i?.scope?.resourceAssignments || [],
          profiles: i?.scope?.monitoringProfileAssignments || [],
          ignoreAlert: i?.overrides?.isAlertDisabled,
          status: i.isEnabled,
          isEnabled: i.isEnabled,
        })) || [];
      setExceptionRules(alertRulesData || []);
      setLoading(false);
      setSaving(false);
    });
  }, [clientId]);

  useEffect(() => {
    getDefinition();
    LookupService.getAlertSeverityTypes(clientId).then((result) => {
      setSeverityList([
        { label: "Select...", value: "" },
        ...result?.data?.severityTypes?.map((i: any) => ({ label: i.name, ...i })),
      ]);
    });

    LookupService.getAlertCategoryTypes(clientId).then((result) => {
      const selectedIds = defaultCategories?.map((i: any) => i.id);
      const initList = result?.data?.categoryTypes?.map((i: any) => ({
        label: i.name,
        ...i,
        ...(selectedIds?.includes(i.id) && { checked: "on" }),
      }));

      setCategoryList(initList);
      setBadgeCategoryList(initList?.filter((i: any) => i.checked === "on"));
    });
  }, [clientId, isEditMode]);

  const handleClose = () => {
    setEditObject(null);
    onClose();
  };

  const resetChanges = () => {
    setSeverityChanged(false);
    setCategoryChanged(false);
    setDescriptionChanged(false);
    setThresholdChanged(false);
    setRecommendationsChanged(false);
    setEditThreshold(null);

    setEditObject(null);
    setCategoryList([]);
    setDescription("");
    setSelectedSeverity("");
    setRecommendations("");
    setThresholds([]);
  };

  const updateHandler = (payload: any, cb: any) => {
    setSaving(true);
    AlertTypesService.updateAlertDefinition({
      clientId,
      alertTypeId: editObject.alertTypeId,
      alertSubtypeId: editObject.alertSubtypeId,
      payload,
    }).then(() => {
      resetChanges();
      cb();
      getDefinition();
    });
  };

  const resetDefaultProps = () => {
    setSelectedSeverity(editObject?.defaultProperties?.severity?.name || NOT_SELECTED);
    setDefaultCategories(editObject?.defaultProperties?.categoryAssignments || []);
    setDescription(editObject?.defaultProperties?.description || NO_DESCRIPTION);
    setSeverityChanged(true);
    setCategoryChanged(true);
    setDescriptionChanged(true);
  };

  const resetRecommendations = () => {
    setRecommendations(editObject?.defaultProperties?.recommendations || "");
    setRecommendationsChanged(true);
  };

  const onSave = () => {
    const payload = {
      properties: {
        ...(isDescriptionChanged && {
          description: {
            action: "Update",
            value: description,
          },
        }),
        ...(isRecommendationsChanged && {
          recommendations: {
            action: "Update",
            value: recommendations,
          },
        }),
        ...(isSeverityChanged && {
          severity: {
            action: "Update",
            value: severityList?.find((i: any) => i.name === selectedSeverity)?.id || 0,
          },
        }),
        ...(isCategoryChanged && {
          categoryAssignments: {
            action: "Update",
            categories: badgeCategoryList.map((i: any) => ({ id: i.id })),
          },
        }),
      },
      ...(thresholdsToUpdate?.length > 0 && {
        thresholds: thresholdsToUpdate.map((i: any) => ({
          action: i?.action ? i?.action : isNewThreshold(i.id) ? "Create" : "Update",
          ...(!isNewThreshold(i.id) && { id: i.id }), //no id on create
          name: i.name,
          values: i.values,
        })),
      }),
    };

    const isUnsavedThresholds = !isNullOrUndefined(editThreshold);

    if (isUnsavedThresholds) {
      setSaveErrors({ error: "Save the tolerance value before proceeding." });
    }

    if (isCustomAlert && !isUnsavedThresholds) {
      handleSaveCustomQueryChanges();
    }

    if (!isUnsavedThresholds && !isCustomAlert) {
      updateHandler(payload, () => {
        setEditMode(false);
      });
    }
  };

  useEffect(() => {
    createRef?.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }, [editThreshold]);

  const handleSave = (params: any, cb: any) => {
    AlertRulesService.updateRule(params).then(() => {
      cb();
      setSelectedRule(null);
      setEditRuleMode(false);
      getDefinition();
    });
  };

  const handleSaveCustomQueryChanges = () => {
    const payload = {
      name: editObject.name,
      properties: {
        description: description,
        recommendations: recommendations,
        severity: {
          id: severityList?.find((i: any) => i.name === selectedSeverity)?.id || 0,
        },
        categoryAssignments: badgeCategoryList.map((i: any) => ({ id: i.id })),
      },
      customQuery: {
        id: customSource.id,
        valueColumnName: customSource.valueColumnName,
        valueName: customSource.valueName,
        objectColumnName: customSource.objectColumnName,
        objectName: customSource.objectName,
        messageInfoColumnName: customSource.messageInfoColumnName,
      },
      operator: customSource.operator,
      evaluationFrequency: customSource.evaluationFrequency,
      thresholds,
      messageFormat: customSource.messageFormat,
    };

    AlertTypesService.updateCustomAlert({
      clientId,
      alertTypeId: editObject.alertTypeId,
      payload,
    }).then(() => {
      resetChanges();
      setEditMode(false);
      getDefinition();
    });
  };

  const handleEnableRule = (ruleId: any) => {
    const payload = {
      isEnabled: true,
    };
    AlertRulesService.updateRuleStatus({ clientId, ruleId, payload }).then(() => {
      getDefinition();
    });
  };

  const handleDeleteRule = (ruleId: any) => {
    setDeleting(true);
    AlertRulesService.deleteRule({ clientId, ruleId }).then(() => {
      setToDeleteSelected(null);
      getDefinition();
      setDeleting(false);
    });
  };

  const getDefaultNumberOfLines = (text: any) => {
    return text.split("\n")?.length <= 6 ? text.split("\n")?.length + 2 : 6;
  };

  const topBlock = (
    <EuiFlexGroup gutterSize="l">
      <EuiFlexItem>
        <EuiText size="s">
          <b>Platform:</b> &nbsp; {editObject?.platformName}
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size="s">
          <b>Resource Type:</b> &nbsp; {editObject?.resourceType}
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size="s">
          <b>Alert Type:</b> &nbsp;{" "}
          {isCustomAlert ? (
            <span
              style={{
                backgroundColor: "#14A05A22",
                color: "#14A05A",
                padding: "4px 8px",
                borderRadius: "4px",
              }}>
              Custom Alert
            </span>
          ) : (
            <span
              style={{
                backgroundColor: "#006bb422",
                color: "#006bb4",
                padding: "4px 8px",
                borderRadius: "4px",
              }}>
              WISdom Alert
            </span>
          )}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const rowRuleActions = {
    title: "Actions",
    buttons: [
      {
        name: "View Rule",
        icon: "eye",
        onClick: (row: any) => {
          setSelectedRule(row);
        },
      },
      ...(isEditModeEnabled
        ? [
            {
              name: "Edit Rule",
              icon: "pencil",
              onClick: (row: any) => {
                setSelectedRule(row);
                setEditRuleMode(true);
              },
            },
            {
              name: "Enable Rule",
              icon: "check",
              onClick: (row: any) => {
                handleEnableRule(row.id);
              },
            },
            {
              name: "Delete Rule",
              buttonProps: {
                color: "danger",
              },
              icon: "trash",
              onClick: (row: any) => {
                handleDeleteRule(row?.id);
              },
            },
          ]
        : []),
    ],
  };

  const rulesBlock = (
    <EuiInMemoryTable
      className={getTableStyles(exceptionRules || [])}
      columns={[
        ...rulesColumns,
        {
          width: "40px",
          name: "",
          align: "right",
          render: (item: any) => {
            return (
              <>
                <FdDataTableRowActions
                  //@ts-ignore
                  Buttons={rowRuleActions.buttons}
                  Row={item}
                  Title={rowRuleActions.title}
                />
              </>
            );
          },
        },
      ]}
      items={exceptionRules}
      pagination={{
        pageSizeOptions: DefaultPageSizeOptions,
        initialPageSize: DEFAULT_NUMBER_OF_ROWS,
      }}
      sorting={{ sort: { field: "name", direction: "desc" } }}
      tableLayout="auto"
    />
  );

  const viewChildren = (
    <EuiFlexGroup direction="column" gutterSize="l">
      <EuiFlexItem className="box">
        <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
          <FDPanelTitle title={selectedItem?.name} />
        </PanelHeader>
        <EuiSpacer size="l" />
        {topBlock}
        <EuiSpacer size="l" />
        <EuiFlexGroup gutterSize="m">
          <EuiFlexItem>
            {!isLoading && (
              <EuiText size="s">
                <b>Severity:</b> &nbsp; {selectedSeverity}
              </EuiText>
            )}
            {isLoading && <SkeletonPlaceholder isLoading={isLoading} />}
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <EuiText size="s">
              <b>Alert Categories:</b> &nbsp;{" "}
              {defaultCategories?.length === 0
                ? "N/A"
                : defaultCategories?.map((c: any) => (
                    <EuiBadge className={classes.chip} color="var(--card-background)" key={c.name}>
                      {c.name}
                    </EuiBadge>
                  ))}
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <EuiText size="s">
              <b>Evaluation Frequency :</b> &nbsp;{editObject?.evaluationFrequency}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="l" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem grow={false}>
            <EuiText size="s">
              <b>Description:</b>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem>
            {!isLoading &&
              description?.split("\n")?.map((d, idx) => (
                <EuiFlexItem key={`description-${idx}`}>
                  {d?.length > 0 && <EuiText size="s">{d}</EuiText>}
                  {d?.length === 0 && (
                    <EuiText size="s">
                      <br></br>
                    </EuiText>
                  )}
                </EuiFlexItem>
              ))}
            {isLoading && <SkeletonPlaceholder isLoading={isLoading} />}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <EuiFlexItem className="box">
        <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
          <FDPanelTitle title="Recommendations" />
        </PanelHeader>
        <EuiSpacer size="l" />
        <EuiFlexGroup direction="column" gutterSize="m">
          {!isLoading &&
            recommendations?.split("\n")?.map((r, idx) => (
              <EuiFlexItem key={`recommendation-${idx}`}>
                {r?.length > 0 && <EuiText size="s">{r}</EuiText>}
                {r?.length === 0 && (
                  <EuiText size="s">
                    <br></br>
                  </EuiText>
                )}
              </EuiFlexItem>
            ))}
          <EuiFlexItem>
            {!isLoading && recommendations?.length === 0 && (
              <>
                <EuiSpacer size="s" />
                <EuiFlexItem>
                  <EuiText size="s">No Recommendation</EuiText>
                </EuiFlexItem>
              </>
            )}
            {isLoading && <SkeletonPlaceholder isLoading={isLoading} />}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      {!isNullOrUndefined(editObject?.formula) && (
        <EuiFlexItem className="box">
          <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
            <div style={{ marginTop: "8px" }}>
              <ToggleButtons
                labels={_TABS.map((tab) => tab.name)}
                defaultSelectedIndex={_TABS.findIndex((tab) => tab.id === selectedTabId)}
                onChange={(selectedIndex: any) => {
                  setSelectedTabId(_TABS[selectedIndex].id);
                }}
                padding="0 0 6px 0"
              />
            </div>
          </PanelHeader>
          <EuiSpacer size="l" />
          {_TABS[0].id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              <EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="s">
                    <b>Alerting Logic</b>
                  </EuiText>
                </EuiFlexItem>
                <EuiSpacer size="s" />
                <EuiFlexItem className="box-regular">
                  <EuiText size="s">{editObject?.formula}</EuiText>
                </EuiFlexItem>
              </EuiFlexItem>
              <EuiSpacer size="xs" />
              {editObject?.formula?.includes("$") && editObject?.alertThresholds?.length > 0 && (
                <EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiText size="s">
                      <b>Tolerance Settings</b>
                    </EuiText>
                  </EuiFlexItem>
                  <EuiSpacer size="s" />
                  {editObject?.alertThresholds?.map((i: any) => (
                    <>
                      <EuiFlexItem className="box-regular">
                        <EuiText size="s">
                          <span
                            style={{
                              color: TOLERANCE_STATUS_COLOR[i.name] || "var(--text)",
                              width: "100px",
                              display: "inline-block",
                            }}>
                            <b>{i.name}:</b>
                          </span>
                          &nbsp;
                          {toleranceToFormula(editObject?.formula, i)}
                        </EuiText>
                      </EuiFlexItem>
                      <EuiSpacer size="m" />
                    </>
                  ))}
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          )}

          {_TABS[1].id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              {rulesBlock}
            </EuiFlexGroup>
          )}
          {_TABS[2]?.id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              <CustomAlerts
                isEditMode={false}
                customSource={customSource}
                setCustomSource={setCustomSource}
                setSelectedTabId={null}
              />
            </EuiFlexGroup>
          )}
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  const editChildren = (
    <EuiFlexGroup direction="column" gutterSize="l">
      <EuiFlexItem className="box">
        <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
          <FDPanelTitle
            title={selectedItem?.name}
            rightActions={
              isCustomAlert ? (
                <span />
              ) : (
                <div>
                  <IconButton
                    aria-label={"Top Instances by Resources"}
                    icon={<UndoIcon />}
                    onClick={resetDefaultProps}
                  />
                </div>
              )
            }
          />
        </PanelHeader>
        <EuiSpacer size="l" />
        {topBlock}
        <EuiSpacer size="l" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b>Severity:</b>
            </EuiText>
            <SelectDropdown
              selectedItem={selectedSeverity}
              options={severityList}
              setOptions={setSeverityList}
              setSelectedItem={(v: any) => {
                setSelectedSeverity(v);
                setSeverityChanged(true);
              }}
              onChange={() => {}}
              isLoading={false}
            />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">
              <b>Alert Categories:</b> &nbsp;{" "}
            </EuiText>
            <SelectDropdown
              selectedItem={""}
              singleSelection={false}
              searchable
              options={categoryList?.map((i: any) => ({ ...i, label: i.name }))}
              setOptions={setCategoryList}
              setSelectedItem={() => {}}
              onChange={() => {
                setCategoryChanged(true);
              }}
              onApply={() => {
                setBadgeCategoryList(categoryList.filter((i: any) => i.checked === "on"));
              }}
              isLoading={false}
            />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s"></EuiText>
            <EuiFlexGroup gutterSize="xs" wrap style={{ marginTop: "20px" }}>
              {badgeCategoryList?.length === 0
                ? ""
                : badgeCategoryList?.map((c: any) => (
                    <EuiFlexItem grow={false}>
                      <EuiBadge
                        className={classes.chip}
                        color="var(--card-background)"
                        key={c.name}
                        iconType="cross"
                        iconSide="right"
                        iconOnClickAriaLabel={c.name}
                        iconOnClick={() => {
                          const filteredItems = categoryList.map((i: any) => {
                            if (i.id !== c.id) return i;
                            return { ...i, checked: undefined };
                          });
                          setCategoryList(filteredItems);

                          setBadgeCategoryList(
                            filteredItems.filter((i: any) => i.checked === "on")
                          );

                          setCategoryChanged(true);
                        }}>
                        {c.name}
                      </EuiBadge>
                    </EuiFlexItem>
                  ))}
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="l" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b>Description:</b>
            </EuiText>
            <EuiTextArea
              id={"description"}
              inputRef={textAreaRef}
              rows={getDefaultNumberOfLines(description)}
              name={"description"}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionChanged(true);
              }}
              readOnly={false}
              value={description}
              isInvalid={false}
              fullWidth
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <EuiFlexItem className="box">
        <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
          <FDPanelTitle
            title="Recommendations"
            rightActions={
              isCustomAlert ? (
                <span />
              ) : (
                <div>
                  <IconButton
                    aria-label={"Top Instances by Resources"}
                    icon={<UndoIcon />}
                    onClick={resetRecommendations}
                  />
                </div>
              )
            }
          />
        </PanelHeader>
        <EuiSpacer size="l" />
        <EuiFlexGroup direction="column" gutterSize="m">
          <EuiTextArea
            id={"recommendations"}
            name={"recommendations"}
            rows={getDefaultNumberOfLines(recommendations)}
            onChange={(e) => {
              setRecommendations(e.target.value);
              setRecommendationsChanged(true);
            }}
            readOnly={false}
            value={recommendations}
            isInvalid={false}
            fullWidth
          />
        </EuiFlexGroup>
      </EuiFlexItem>

      {!isNullOrUndefined(editObject?.formula) && (
        <EuiFlexItem className="box">
          <PanelHeader className={classes.flyoutDetailsPanel} showBottomSpace={false}>
            <div style={{ marginTop: "8px" }}>
              <ToggleButtons
                labels={_TABS.map((tab) => tab.name)}
                defaultSelectedIndex={_TABS.findIndex((tab) => tab.id === selectedTabId)}
                onChange={(selectedIndex: any) => {
                  setSelectedTabId(_TABS[selectedIndex].id);
                }}
                padding="0 0 6px 0"
              />
            </div>
          </PanelHeader>
          <EuiSpacer size="l" />
          {_TABS[0].id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              <EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="s">
                    <b>Alerting Logic</b>
                  </EuiText>
                </EuiFlexItem>
                <EuiSpacer size="s" />
                <EuiFlexItem className="box-regular">
                  <EuiText size="s">{editObject?.formula}</EuiText>
                </EuiFlexItem>
              </EuiFlexItem>
              <EuiSpacer size="xs" />
              {editObject?.formula?.includes("$") && (
                <EuiFlexGroup direction="column" gutterSize="m">
                  <EuiFlexItem>
                    <EuiFlexGroup gutterSize="m" justifyContent="spaceBetween" alignItems="center">
                      <EuiFlexItem grow={false}>
                        <EuiText size="s">
                          <b>Tolerance Settings</b>
                        </EuiText>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <AddButton
                          text={"Tolerance"}
                          onClick={() => {
                            const newThreshold = {
                              id: `new_${thresholds?.length + 1}`,
                              name: "",
                              values:
                                thresholdsTemplate[0]?.values?.map((v: any) => ({
                                  ...v,
                                  value: v?.key === "MessageRegexFilter" ? ".*" : "",
                                })) || [],
                              modificationRules: { canRename: true },
                            };

                            setThresholds([...thresholds, newThreshold]);
                            setEditThreshold(newThreshold);
                            setThresholdChanged(true);
                          }}
                          isDisabled={
                            thresholdsTemplate?.length === 0 || !isNullOrUndefined(editThreshold)
                          }
                        />
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiFlexItem>

                  <EuiSpacer size="xs" />
                  {thresholds?.length > 0 &&
                    thresholds
                      ?.filter((t: any) => t?.action !== "Delete")
                      ?.map((i: any) => (
                        <div ref={i.id === editThreshold?.id ? createRef : null}>
                          <Card
                            borderColor={
                              i.id === editThreshold?.id ? "var(--blue)" : "var(--border-color)"
                            }
                            borderPosition={"all"}
                            children={
                              <EuiFlexItem>
                                <EuiFlexGroup
                                  gutterSize="m"
                                  justifyContent="spaceBetween"
                                  alignItems="center">
                                  <EuiFlexItem>
                                    <EuiText size="s">
                                      <span
                                        style={{
                                          color: TOLERANCE_STATUS_COLOR[i.name] || "var(--text)",
                                          width: "150px",
                                          display: "inline-block",
                                        }}>
                                        {i.id === editThreshold?.id &&
                                        i?.modificationRules?.canRename ? (
                                          <EuiFormRow
                                            label={""}
                                            fullWidth
                                            className={"row-required"}
                                            error={"Name already taken."}
                                            isInvalid={showError}>
                                            <EuiFieldText
                                              id={"threshold_name"}
                                              min={0}
                                              placeholder={
                                                isNewThreshold(i.id) ? "Enter name" : i.name
                                              }
                                              onChange={(e) => {
                                                setEditThreshold({
                                                  ...editThreshold,
                                                  name: e.target.value,
                                                });

                                                setShowError(
                                                  thresholds?.some(
                                                    (i: any) =>
                                                      i.name.trim() === e.target.value.trim()
                                                  )
                                                );

                                                setThresholdChanged(true);
                                              }}
                                              value={editThreshold?.name || ""}
                                            />
                                          </EuiFormRow>
                                        ) : (
                                          <b>{i.name}:</b>
                                        )}
                                      </span>
                                      &nbsp;
                                      {i.id === editThreshold?.id
                                        ? editObject?.formula?.split(" ")?.map((i: any) => {
                                            const match = editThreshold?.values.find((v: any) =>
                                              i.includes(v.key)
                                            );
                                            if (isNullOrUndefined(match))
                                              return <span>{`${i} `}</span>; //space is required
                                            return (
                                              <>
                                                {i !== "$Operator" &&
                                                  i !== "$MessageRegexFilter" && (
                                                    <span
                                                      style={{
                                                        width: "80px",
                                                        display: "inline-block",
                                                        paddingRight: "8px",
                                                      }}>
                                                      <EuiFieldNumber
                                                        id={i.key}
                                                        min={0}
                                                        placeholder={i.value}
                                                        onChange={(e) => {
                                                          setEditThreshold({
                                                            ...editThreshold,
                                                            values: editThreshold?.values?.map(
                                                              (i: any) => {
                                                                if (i.key === match.key) {
                                                                  return {
                                                                    ...i,
                                                                    value: e.target.value,
                                                                  };
                                                                }
                                                                return i;
                                                              }
                                                            ),
                                                          });

                                                          setThresholdChanged(true);
                                                        }}
                                                        value={match.value}
                                                      />
                                                    </span>
                                                  )}

                                                {i === "$Operator" && (
                                                  <span
                                                    style={{
                                                      width: "60px",
                                                      display: "inline-block",
                                                      paddingRight: "4px",
                                                    }}>
                                                    <SelectDropdown
                                                      width={112}
                                                      isPanelWidth
                                                      searchable={false}
                                                      hideCheckbox
                                                      selectedItem={match.value}
                                                      options={operatorList}
                                                      setOptions={setOperatorList}
                                                      setSelectedItem={(v: any) => {
                                                        setEditThreshold({
                                                          ...editThreshold,
                                                          values: editThreshold?.values?.map(
                                                            (i: any) => {
                                                              if (i.key === match.key) {
                                                                return {
                                                                  ...i,
                                                                  value: v,
                                                                };
                                                              }
                                                              return i;
                                                            }
                                                          ),
                                                        });
                                                      }}
                                                      onChange={() => {}}
                                                      isLoading={false}
                                                    />
                                                  </span>
                                                )}
                                                {i === "$MessageRegexFilter" && (
                                                  <span
                                                    style={{
                                                      width: "140px",
                                                      display: "inline-block",
                                                      paddingRight: "8px",
                                                    }}>
                                                    <EuiFieldText
                                                      id={i.key}
                                                      min={0}
                                                      placeholder={i.value}
                                                      onChange={(e) => {
                                                        setEditThreshold({
                                                          ...editThreshold,
                                                          values: editThreshold?.values?.map(
                                                            (i: any) => {
                                                              if (i.key === match.key) {
                                                                return {
                                                                  ...i,
                                                                  value: e.target.value,
                                                                };
                                                              }
                                                              return i;
                                                            }
                                                          ),
                                                        });

                                                        setThresholdChanged(true);
                                                      }}
                                                      value={match.value}
                                                    />
                                                  </span>
                                                )}
                                              </>
                                            ); //match.value;
                                          })
                                        : toleranceToFormula(editObject?.formula, i)}
                                    </EuiText>
                                  </EuiFlexItem>
                                  <EuiFlexItem grow={false}>
                                    {i.id !== editThreshold?.id && (
                                      <ActionButtons
                                        buttons={[
                                          {
                                            type: "pencil",
                                            onClick: () => {
                                              setEditThreshold(i);
                                            },
                                          },
                                          ...(i?.modificationRules?.canDelete ||
                                          isNewThreshold(i?.id)
                                            ? [
                                                {
                                                  type: "trash",
                                                  onClick: () => {
                                                    if (isNewThreshold(i?.id)) {
                                                      setThresholds(
                                                        thresholds.filter((t: any) => i.id !== t.id)
                                                      );
                                                    } else {
                                                      setThresholds(
                                                        thresholds.map((t: any) => {
                                                          if (t.id === i?.id) {
                                                            return {
                                                              action: "Delete",
                                                              ...t,
                                                              modificationRules: {
                                                                canRename: true,
                                                                canDelete: true,
                                                              },
                                                            };
                                                          }
                                                          return t;
                                                        })
                                                      );
                                                    }
                                                    setThresholdChanged(true);
                                                  },
                                                },
                                              ]
                                            : []),
                                        ]}
                                      />
                                    )}
                                    {i.id === editThreshold?.id && (
                                      <ActionButtons
                                        buttons={[
                                          ...(i?.modificationRules?.canRevert
                                            ? [
                                                {
                                                  icon: <UndoIcon />,
                                                  onClick: () => {
                                                    setThresholds(
                                                      thresholds.map((t: any) => {
                                                        if (t.id === i.id) {
                                                          return {
                                                            action: "Revert",
                                                            ...t,
                                                            values: t.defaultValues,
                                                          };
                                                        }
                                                        return t;
                                                      })
                                                    );

                                                    setThresholdChanged(true);
                                                    setEditThreshold(null);
                                                  },
                                                },
                                              ]
                                            : []),
                                          {
                                            type: "save",
                                            disabled:
                                              showError ||
                                              editThreshold?.name?.length === 0 ||
                                              editThreshold?.values
                                                ?.map((i: any) =>
                                                  i.key === "MessageRegexFilter" &&
                                                  i.value?.length === 0
                                                    ? ".*"
                                                    : i.value
                                                )
                                                ?.some((i: any) => i?.length === 0),
                                            onClick: () => {
                                              setThresholds(
                                                thresholds.map((t: any) => {
                                                  if (t.id === editThreshold?.id) {
                                                    return editThreshold?.id === null
                                                      ? {
                                                          ...{
                                                            ...editThreshold,
                                                            values: editThreshold?.values?.map(
                                                              (i: any) => ({
                                                                ...i,
                                                                value:
                                                                  i.key === "MessageRegexFilter" &&
                                                                  i.value?.length === 0
                                                                    ? ".*"
                                                                    : i.value,
                                                              })
                                                            ),
                                                          },
                                                          modificationRules: {
                                                            canRename: true,
                                                            canDelete: true,
                                                          },
                                                        }
                                                      : editThreshold;
                                                  }
                                                  return t;
                                                })
                                              );

                                              setThresholdChanged(true);
                                              setEditThreshold(null);
                                              setSaveErrors({});
                                            },
                                          },
                                          {
                                            type: "cross",
                                            onClick: () => {
                                              setSaveErrors({});
                                              if (i.name?.length === 0) {
                                                setThresholds(
                                                  thresholds.filter(
                                                    (t: any) => t.id !== editThreshold?.id
                                                  )
                                                );
                                              }

                                              setEditThreshold(null);
                                            },
                                          },
                                        ]}
                                      />
                                    )}
                                  </EuiFlexItem>
                                </EuiFlexGroup>
                              </EuiFlexItem>
                            }
                          />
                          <EuiSpacer size="m" />
                        </div>
                      ))}
                </EuiFlexGroup>
              )}
            </EuiFlexGroup>
          )}

          {_TABS[1].id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              {rulesBlock}
            </EuiFlexGroup>
          )}

          {_TABS[2]?.id === selectedTabId && (
            <EuiFlexGroup direction="column" gutterSize="m">
              <CustomAlerts
                isEditMode
                customSource={customSource}
                setCustomSource={setCustomSource}
                setSelectedTabId={null}
              />
            </EuiFlexGroup>
          )}
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  return (
    <Flyout
      zIndex={9003}
      title={isEditMode ? "Edit Alert Type" : "Alert Type Details"}
      onClose={handleClose}
      children={isEditMode ? editChildren : viewChildren}
      footerChildren={
        <>
          {!isNullOrUndefined(selectedRule) && (
            <AlertRulesDetailsFlyout
              selectedItem={selectedRule}
              onClose={() => {
                setSelectedRule(null);
                setEditRuleMode(false);
              }}
              isNew={false}
              editMode={isEditRuleMode}
              profileOptions={profileOptions}
              onSave={handleSave}
            />
          )}

          <ErrorsList errors={saveErrors} />

          {!isEditMode && (
            <EuiFlexGroup justifyContent="flexEnd">
              {isEditModeEnabled && canEdit && (
                <EuiFlexItem grow={false}>
                  <Button
                    type="secondary"
                    text={"Edit"}
                    onClick={() => {
                      setEditMode(true);
                    }}
                  />
                </EuiFlexItem>
              )}

              <EuiFlexItem grow={false}>
                <Button type="primary" text={"Close"} onClick={handleClose} />
              </EuiFlexItem>
            </EuiFlexGroup>
          )}

          {isEditMode && (
            <EuiFlexGroup justifyContent="flexEnd">
              <EuiFlexItem grow={false}>
                <Button
                  type="secondary"
                  text={"Cancel"}
                  onClick={() => {
                    setEditMode(false);
                    resetChanges();
                  }}
                />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <Button
                  type="primary"
                  text={"Save"}
                  onClick={onSave}
                  isLoading={isSaving}
                  disabled={!isSaveEnabled}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
        </>
      }
    />
  );
};
